/**
 * Mục "Ưu đãi tại quán" — carousel cuộn ngang, snap từng thẻ.
 *
 * Trang chỉ chạy bề ngang điện thoại nên luôn là một thẻ rưỡi mỗi lượt: thẻ
 * sau lộ ra một phần là dấu hiệu duy nhất cho biết còn ưu đãi nữa mà không tốn
 * thêm gì. Còn đúng một ưu đãi thì bỏ carousel, bỏ dots — không có gì để cuộn.
 */
import React, { useCallback, useRef, useState } from "react";
import PromotionCard from "./PromotionCard";
import { Promotion } from "../../interfaces/venue";

interface Props {
  promotions: Promotion[];
  hotline: string;
}

/** Khoảng cách giữa hai thẻ, khớp với `gap` của .vn-promos trong venue.css. */
const GAP = 12;

const PromotionSection: React.FC<Props> = ({ promotions, hotline }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  const single = promotions.length <= 1;
  const lastIndex = Math.max(0, promotions.length - 1);

  const go = useCallback(
    (target: number) => {
      const track = trackRef.current;
      const next = Math.min(Math.max(target, 0), lastIndex);
      setIndex(next);
      const card = track?.children[next] as HTMLElement | undefined;
      if (track && card) {
        track.scrollTo({
          left: card.offsetLeft - track.offsetLeft,
          behavior: "smooth",
        });
      }
    },
    [lastIndex]
  );

  // Vuốt tay cũng phải cập nhật dots, nên vị trí đọc ngược lại từ scrollLeft.
  const onScroll = () => {
    const track = trackRef.current;
    if (!track || track.children.length === 0) {
      return;
    }
    const step = (track.children[0] as HTMLElement).offsetWidth + GAP;
    setIndex(Math.min(Math.round(track.scrollLeft / step), lastIndex));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(index + 1);
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(index - 1);
    }
  };

  return (
    <section className="vn-section" id="uu-dai">
      <h2 className="vn-section-title">Ưu đãi tại quán</h2>
      <p className="vn-section-sub">{promotions.length} ưu đãi đang áp dụng</p>

      <div
        className={`vn-promos${single ? " single" : ""}`}
        ref={trackRef}
        onScroll={single ? undefined : onScroll}
        onKeyDown={onKeyDown}
        role="region"
        aria-label="Ưu đãi tại quán"
        tabIndex={0}
      >
        {promotions.map((promotion) => (
          <PromotionCard
            key={promotion.id}
            promotion={promotion}
            hotline={hotline}
          />
        ))}
      </div>

      {single ? null : (
        <div className="vn-dots">
          {promotions.map((promotion, i) => (
            <button
              key={promotion.id}
              type="button"
              className="vn-dot-hit"
              onClick={() => go(i)}
              aria-label={`Tới ưu đãi thứ ${i + 1}`}
              aria-current={i === index ? "true" : undefined}
            >
              <span className={`vn-dot${i === index ? " active" : ""}`} />
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default PromotionSection;
